import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { errorMessage, buttonGhost, buttonPrimary, inputClass } from "./aiRoutes";

export default function ProviderCredentialsPanel({ settings, onSaved }) {
  const [replacing, setReplacing] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);
  const [results, setResults] = useState({});
  const providers = settings?.provider_status || [];

  const saveKey = async (provider) => {
    setSaving(provider.provider);
    try {
      await api.put(`/api/ai/settings/providers/${provider.provider}/key`, { api_key: values[provider.provider] || "" });
      setValues((current) => ({ ...current, [provider.provider]: "" }));
      setReplacing((current) => ({ ...current, [provider.provider]: false }));
      toast.success(`${provider.label} key saved`);
      await onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, "Could not save provider key"));
    } finally {
      setSaving(null);
    }
  };

  const removeKey = async (provider) => {
    setSaving(provider.provider);
    try {
      await api.delete(`/api/ai/settings/providers/${provider.provider}/key`);
      toast.success(`${provider.label} vault key removed`);
      await onSaved?.();
    } catch (error) {
      toast.error(errorMessage(error, "Could not remove provider key"));
    } finally {
      setSaving(null);
    }
  };

  const testConnection = async (provider) => {
    setTesting(provider.provider);
    try {
      const { data } = await api.post("/api/ai/settings/test", { provider: provider.provider, model: provider.default_model });
      setResults((current) => ({ ...current, [provider.provider]: data }));
    } catch (error) {
      setResults((current) => ({ ...current, [provider.provider]: { ok: false, error: errorMessage(error, "Connection test failed") } }));
    } finally {
      setTesting(null);
    }
  };

  if (!settings) return <div className="text-sm text-zinc-500">No providers loaded.</div>;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {providers.map((provider) => {
        const status = results[provider.provider];
        return (
          <section key={provider.provider} className="border border-zinc-800 rounded-lg p-4 bg-zinc-950/40">
            <header className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-zinc-100 text-sm">{provider.label}</h3>
                <p className="text-xs text-zinc-500 mt-1">{provider.configured ? `Credential from ${provider.key_source}` : `Set ${provider.env_key} or add a server-side key`}</p>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded border ${provider.configured ? "border-emerald-700/50 text-emerald-300 bg-emerald-900/20" : "border-amber-700/50 text-amber-300 bg-amber-900/20"}`}>
                {provider.configured ? "Configured" : "Missing credentials"}
              </span>
            </header>
            <dl className="mt-3 text-xs">
              <dt className="text-zinc-500">API key</dt><dd className="text-zinc-300">{provider.key_masked || "Not configured"}</dd>
              <dt className="text-zinc-500 mt-2">Default model</dt><dd className="text-zinc-300">{provider.default_model}</dd>
            </dl>
            {!replacing[provider.provider] ? (
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" className={buttonGhost} onClick={() => setReplacing((current) => ({ ...current, [provider.provider]: true }))}>{provider.configured ? "Replace key" : "Add key"}</button>
                <button type="button" className={buttonGhost} disabled={!provider.configured || testing === provider.provider} onClick={() => testConnection(provider)}>{testing === provider.provider ? "Testing…" : "Test connection"}</button>
                {provider.key_source === "vault" && <button type="button" className={buttonGhost} disabled={saving === provider.provider} onClick={() => removeKey(provider)}>Remove vault key</button>}
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                <label className="block text-xs text-zinc-400" htmlFor={`provider-key-${provider.provider}`}>Replacement API key</label>
                <input id={`provider-key-${provider.provider}`} type="password" autoComplete="new-password" className={inputClass} value={values[provider.provider] || ""} onChange={(event) => setValues((current) => ({ ...current, [provider.provider]: event.target.value }))} />
                <div className="flex gap-2">
                  <button type="button" className={buttonPrimary} disabled={saving === provider.provider || !values[provider.provider]?.trim()} onClick={() => saveKey(provider)}>{saving === provider.provider ? "Saving…" : "Save key"}</button>
                  <button type="button" className={buttonGhost} onClick={() => setReplacing((current) => ({ ...current, [provider.provider]: false }))}>Cancel</button>
                </div>
              </div>
            )}
            {status && <p className={`mt-3 text-xs ${status.ok ? "text-emerald-300" : "text-red-300"}`}>{status.ok ? `Connection succeeded in ${status.latency_ms} ms` : `Connection failed${status.http_status ? ` (HTTP ${status.http_status})` : ""}: ${status.error || status.error_code}`}</p>}
          </section>
        );
      })}
    </div>
  );
}
